import api from './api';
import clienteService from './clienteService';
import productoService from './productoService';
import transporteService from './transporteService';
import movMonedaService from './movMonedaService';

const DEFAULT_NRO_SUC = 1;

export interface CotizacionVentaDetalle {
    nroIdformulario: number;
    nroItem: number;
    codigoArticulo?: string;
    descripcionArticulo?: string;
    cantidad?: number;
    precioUnitario?: number;
    precioLista?: number;
    descuento?: number;
    porcDesc?: number;
    subtotal?: number;
    notas?: string;
    um?: string;
    codigoMoneda?: number;
    cotizacion?: number;
}

export interface CotizacionVenta {
    nroFormulario: number;
    nroTipoFormulario: string;
    tipoCliente: string;
    fechaEmision: Date;
    horaEmision: Date;
    nroCliente: number;
    nombre: string;
    nroVendedor: number;
    nroLista: string;
    codigoMoneda: string | number;
    importeTotal: number;
    asentado: string;
    descripcion: string;
    usuario: string;
    detalles: CotizacionVentaDetalle[];
    porcDesc?: number | null;
    porcDesc2?: number | null;
    porcDesc3?: number | null;
    estado?: string;
    letra?: string;
    formaDePago?: string;
    nroSuc?: number;
    nroComprobante?: number;
    ordenDeCompra?: string;
    nroUbicacionOrigen?: number;
    nroExpreso?: number;
}

export interface CotizacionVentaSearchParams {
    searchTerm?: string;
    pageNumber: number;
    pageSize: number;
    fechaDesde?: Date;
    fechaHasta?: Date;
    nroCliente?: string;
    nroVendedor?: string;
    asentado?: string;
    estado?: string;
    usuario?: string;
}

export interface CotizacionVentaPagedResult {
    items: CotizacionVenta[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
}

const cotizacionVentaService = {
    getCotizacionesVenta: async (params: CotizacionVentaSearchParams): Promise<CotizacionVentaPagedResult> => {
        const response = await api.get<CotizacionVentaPagedResult>('/api/CotizacionesVenta', { params });
        return response.data;
    },

    getCotizacionVenta: async (id: number): Promise<CotizacionVenta> => {
        const response = await api.get<CotizacionVenta>(`/api/CotizacionesVenta/${id}`);
        return response.data;
    },

    createCotizacionVenta: async (cotizacion: CotizacionVenta & {
        nroSuc?: number;
        ordenCompra?: string;
        transporte?: string;
        ubicacion?: string;
        formaDePago?: string;
        idCompania?: string;
    }): Promise<CotizacionVenta> => {
        const errors: string[] = [];

        if (!cotizacion.nroCliente || cotizacion.nroCliente <= 0) {
            errors.push('nroCliente es obligatorio y debe ser mayor a 0');
        }

        let clienteData: any = null;
        if (cotizacion.nroCliente && cotizacion.nroCliente > 0) {
            try {
                clienteData = await clienteService.getCliente(cotizacion.nroCliente);
                if (!clienteData) {
                    errors.push(`No se encontró el cliente con número ${cotizacion.nroCliente}`);
                }
            } catch (error: any) {
                const mensajeError = error?.response?.status === 404
                    ? `No se encontró el cliente con número ${cotizacion.nroCliente}`
                    : `Error al obtener los datos del cliente: ${error?.message || 'Error desconocido'}`;
                errors.push(mensajeError);
            }
        }

        if (!cotizacion.detalles || cotizacion.detalles.length === 0) {
            errors.push('Debe haber al menos un item en la cotización');
        }

        const productosData: Record<string, any> = {};
        if (cotizacion.detalles && cotizacion.detalles.length > 0) {
            for (const detalle of cotizacion.detalles) {
                if (detalle.codigoArticulo) {
                    try {
                        const producto = await productoService.getProducto(detalle.codigoArticulo);
                        if (!producto) {
                            errors.push(`No se encontró el producto con código "${detalle.codigoArticulo}"`);
                        } else {
                            productosData[detalle.codigoArticulo] = producto;
                        }
                    } catch (error: any) {
                        const itemIndex = cotizacion.detalles.findIndex(d => d.codigoArticulo === detalle.codigoArticulo) + 1;
                        const mensajeError = error?.response?.status === 404
                            ? `Item ${itemIndex}: No se encontró el producto con código "${detalle.codigoArticulo}"`
                            : `Item ${itemIndex}: Error al obtener el producto "${detalle.codigoArticulo}": ${error?.message || 'Error desconocido'}`;
                        errors.push(mensajeError);
                    }
                }

                cotizacion.detalles.forEach((detalle, index) => {
                    const itemNum = index + 1;
                    if (!detalle.codigoArticulo || String(detalle.codigoArticulo).trim() === '') {
                        errors.push(`Item ${itemNum}: codigoArticulo es obligatorio`);
                    }
                    if (!detalle.descripcionArticulo || String(detalle.descripcionArticulo).trim() === '') {
                        errors.push(`Item ${itemNum}: descripcion es obligatoria`);
                    }
                    if (detalle.cantidad === undefined || detalle.cantidad === null) {
                        errors.push(`Item ${itemNum}: cantidad es obligatoria`);
                    } else if (typeof detalle.cantidad !== 'number' || isNaN(detalle.cantidad)) {
                        errors.push(`Item ${itemNum}: cantidad debe ser un número válido`);
                    } else if (detalle.cantidad <= 0) {
                        errors.push(`Item ${itemNum}: cantidad debe ser mayor a 0`);
                    }
                });
            }
        }

        if (!cotizacion.fechaEmision) {
            errors.push('La Fecha de Emisión es obligatoria.');
        }

        if (errors.length > 0) {
            const errorMessage = errors.length === 1
                ? errors[0]
                : `Se encontraron ${errors.length} errores:\n${errors.map((e, i) => `${i + 1}. ${e}`).join('\n')}`;
            throw new Error(errorMessage);
        }

        let cotizacionUSD: number | null = null;
        try {
            const cot = await movMonedaService.getUltimaCotizacion();
            if (cot && Number.isFinite(cot) && cot > 0) cotizacionUSD = cot;
        } catch { /* ignore */ }

        const items = cotizacion.detalles.map((detalle) => {
            const productoData = productosData[detalle.codigoArticulo || ''];
            let precioUnitario = detalle.precioUnitario ?? 0;
            if (!precioUnitario || precioUnitario <= 0) {
                const precioProducto = productoData?.precioVenta ?? 0;
                if (precioProducto && precioProducto > 0) {
                    const tipoMonedaProd = productoData?.tipoMoneda ?? productoData?.tipoMonedaArticulo ?? 0;
                    if (Number(tipoMonedaProd) === 1 && cotizacionUSD && cotizacionUSD > 0) {
                        precioUnitario = Number((precioProducto * cotizacionUSD).toFixed(2));
                    } else {
                        precioUnitario = Number(precioProducto);
                    }
                }
            }
            const porcDesc = detalle.descuento ?? detalle.porcDesc ?? productoData?.porc_Desc ?? productoData?.porcDesc ?? 0;

            return {
                codigoArticulo: String(detalle.codigoArticulo ?? '').trim(),
                descripcion: String(detalle.descripcionArticulo ?? productoData?.descripcion ?? '').trim(),
                cantidad: Number(detalle.cantidad ?? 0.01),
                precioUnitario: Number(precioUnitario),
                porcDesc: Number(porcDesc),
                um: detalle.um ?? '',
                codigoMoneda: 0,
                cotizacion: 0,
                notas: detalle.notas ?? ''
            };
        });

        let nroUbicacionOrigen = 0;
        if ((cotizacion as any).ubicacion) {
            try {
                const ubicacionStr = String((cotizacion as any).ubicacion);
                const match = ubicacionStr.match(/\((\d+)\)/);
                if (match && match[1]) nroUbicacionOrigen = Number(match[1]);
            } catch { /* ignore */ }
        }

        let formaEntrega: string | null = null;
        const transporteSeleccionado = String((cotizacion as any).transporte ?? '').trim();
        if (transporteSeleccionado) {
            try {
                const transporteEncontrado = await transporteService.getTransporte(transporteSeleccionado);
                if (transporteEncontrado) {
                    const formaEntregaRaw = transporteEncontrado.formaEntrega ?? (transporteEncontrado as any).formaEntrega;
                    if (formaEntregaRaw !== null && formaEntregaRaw !== undefined) {
                        formaEntrega = String(formaEntregaRaw).trim() || null;
                    }
                }
            } catch { /* ignore */ }
        }

        const fechaEmisionDate = cotizacion.fechaEmision ? new Date(cotizacion.fechaEmision) : new Date();
        const fechaEmisionISO = fechaEmisionDate.toISOString();

        const getPorcentajeBonificacion = (...values: Array<number | string | null | undefined>) => {
            for (const value of values) {
                if (value === null || value === undefined) continue;
                const num = Number(value);
                if (Number.isFinite(num)) return Math.max(0, Math.min(100, num));
            }
            return 0;
        };

        const porcDescValue = getPorcentajeBonificacion((cotizacion as any).porcDesc, clienteData?.porcDesc, (clienteData as any)?.porc_Desc);
        const porcDesc2Value = getPorcentajeBonificacion((cotizacion as any).porcDesc2, clienteData?.porcDesc2, (clienteData as any)?.porc_Desc2);
        const porcDesc3Value = getPorcentajeBonificacion((cotizacion as any).porcDesc3, clienteData?.porcDesc3, (clienteData as any)?.porc_Desc3);

        const nroSucValueRaw = (cotizacion as any).nroSuc;
        const nroSucValue = Number.isFinite(Number(nroSucValueRaw)) && Number(nroSucValueRaw) > 0 ? Number(nroSucValueRaw) : DEFAULT_NRO_SUC;

        const formulario: any = {
            descripcion: String(cotizacion.descripcion ?? '').trim(),
            nroSuc: nroSucValue,
            fechaEmision: fechaEmisionISO,
            horaEmision: fechaEmisionISO,
            fechaVencim: fechaEmisionISO,
            nroCliente: Number(clienteData?.nroCliente ?? cotizacion.nroCliente ?? 0),
            nroLista: String(clienteData?.nroLista ?? cotizacion.nroLista ?? '').trim(),
            formaDePago: String((cotizacion as any).formaDePago ?? '').trim(),
            nroUbicacionOrigen: Number(nroUbicacionOrigen),
            ordenDeCompra: String((cotizacion as any).ordenCompra ?? '').trim(),
            codigoDireccion: '',
            codigoDireccionEnvio: '',
            direccionEnvio: '',
            formaEntrega: formaEntrega ?? '',
            porcDesc: porcDescValue,
            porcDesc2: porcDesc2Value,
            porcDesc3: porcDesc3Value
        };

        const tipoIVA = (cotizacion as any).tipoIVA || clienteData?.tipoIva || '';
        const esConsumidorFinal = tipoIVA === 'C' || tipoIVA === 'CF';
        const valorCuit = esConsumidorFinal
            ? ((cotizacion as any).dni || (clienteData as any)?.dni || '')
            : ((cotizacion as any).cuit || clienteData?.cuit || '');
        if (valorCuit && String(valorCuit).trim() !== '') {
            formulario.cuit = String(valorCuit).trim();
        }

        const codigoCiudadEnvioValue = (cotizacion as any).codigoCiudadEnvio;
        if (codigoCiudadEnvioValue !== undefined && codigoCiudadEnvioValue !== null && codigoCiudadEnvioValue !== 0) {
            formulario.codigoCiudadEnvio = Number(codigoCiudadEnvioValue);
        }

        const idCompaniaValue = (cotizacion as any).idCompania;
        if (idCompaniaValue !== undefined && idCompaniaValue !== null && String(idCompaniaValue).trim() !== '') {
            formulario.idCompania = String(idCompaniaValue).trim();
        }

        const payload = { formulario, items };
        const response = await api.post<CotizacionVenta>('/api/CotizacionesVenta', payload);
        return response.data;
    },

    updateCotizacionVenta: async (id: number, cotizacion: CotizacionVenta & {
        nroSuc?: number;
        ordenCompra?: string;
        transporte?: string;
        ubicacion?: string;
        formaDePago?: string;
    }): Promise<CotizacionVenta> => {
        let nroUbicacionOrigen: number | undefined = undefined;
        if ((cotizacion as any).ubicacion) {
            try {
                const ubicacionStr = String((cotizacion as any).ubicacion);
                const match = ubicacionStr.match(/\((\d+)\)/);
                if (match && match[1]) nroUbicacionOrigen = Number(match[1]);
            } catch { /* ignore */ }
        }

        const round5 = (n: number) => Math.round(n * 100000) / 100000;

        const items = (cotizacion.detalles || []).map((detalle) => {
            const precioLista = detalle.precioUnitario ?? detalle.precioLista ?? 0;
            const descuentoPorcentaje = detalle.descuento ?? detalle.porcDesc ?? 0;
            return {
                codigoArticulo: detalle.codigoArticulo || '',
                descripcion: detalle.descripcionArticulo || '',
                cantidad: Number(detalle.cantidad || 0),
                precioUnitario: round5(precioLista),
                porcDesc: Number(descuentoPorcentaje),
                notas: detalle.notas || ''
            };
        });

        const fechaEmisionDate = cotizacion.fechaEmision ? (cotizacion.fechaEmision instanceof Date ? cotizacion.fechaEmision : new Date(cotizacion.fechaEmision)) : new Date();
        const fechaEmisionISO = fechaEmisionDate.toISOString();

        const formulario = {
            descripcion: cotizacion.descripcion || '',
            nroSuc: (cotizacion as any).nroSuc ?? DEFAULT_NRO_SUC,
            fechaEmision: fechaEmisionISO,
            horaEmision: cotizacion.horaEmision ? (cotizacion.horaEmision instanceof Date ? cotizacion.horaEmision : new Date(cotizacion.horaEmision)).toISOString() : fechaEmisionISO,
            fechaVencim: fechaEmisionISO,
            nroLista: cotizacion.nroLista || '',
            formaDePago: ((cotizacion as any).formaDePago || '').trim(),
            nroUbicacionOrigen: nroUbicacionOrigen ?? 0,
            ordenDeCompra: ((cotizacion as any).ordenCompra || (cotizacion as any).ordenDeCompra || '').trim(),
            porcDesc: cotizacion.porcDesc ?? 0,
            porcDesc2: cotizacion.porcDesc2 ?? 0,
            porcDesc3: cotizacion.porcDesc3 ?? 0
        };

        const payload = { formulario, items };
        const response = await api.put<CotizacionVenta>(`/api/CotizacionesVenta/${id}`, payload);
        return response.data;
    },

    getCotizacionVentaPDF: async (id: number): Promise<Blob> => {
        const response = await api.get(`/api/CotizacionesVenta/${id}/pdf`, {
            responseType: 'blob'
        });
        return response.data;
    },

    anularCotizacion: async (id: number): Promise<void> => {
        await api.post(`/api/CotizacionesVenta/${id}/anular`);
    }
};

export default cotizacionVentaService;
